import { HatElement } from '@/components/hat-element.js';
import jsQR from 'jsqr';

class HatQrReader extends HatElement {
    private _stream: MediaStream | null = null;
    private _scanTimer: number | null = null;
    private _detector: BarcodeDetector | null = null;
    private _canvas: HTMLCanvasElement | null = null;

    connectedCallback() {
    super.connectedCallback();
    this._renderShell();
        this._start();
    }

    disconnectedCallback() {
    super.disconnectedCallback();
    this._stop();
    }

    // ─── Rendering ────────────────────────────────────────────────────────────

    _renderShell() {
        this.root.innerHTML = `
            <style>
                .wrapper {
                    position: relative;
                    display: inline-block;
                    width: 100%;
                }

                video {
                    width: 100%;
                    border-radius: 12px;
                    display: block;
                }

                .viewfinder {
                    position: absolute;
                    inset: 0;
                    pointer-events: none;
                }

                .corner {
                    position: absolute;
                    width: 24px;
                    height: 24px;
                    border-color: white;
                    border-style: solid;
                    opacity: 0.9;
                }

                .corner.tl { top: 14%; left: 14%; border-width: 3px 0 0 3px; border-radius: 4px 0 0 0; }
                .corner.tr { top: 14%; right: 14%; border-width: 3px 3px 0 0; border-radius: 0 4px 0 0; }
                .corner.bl { bottom: 14%; left: 14%; border-width: 0 0 3px 3px; border-radius: 0 0 0 4px; }
                .corner.br { bottom: 14%; right: 14%; border-width: 0 3px 3px 0; border-radius: 0 0 4px 0; }

                .scan-line {
                    position: absolute;
                    left: 14%;
                    right: 14%;
                    height: 2px;
                    background: rgba(255, 255, 255, 0.7);
                    animation: scan 2s ease-in-out infinite;
                    top: 14%;
                }

                @keyframes scan {
                    0%   { top: 14%; }
                    50%  { top: 86%; }
                    100% { top: 14%; }
                }

                .unsupported {
                    padding: 20px;
                    color: #666;
                    font-size: 14px;
                    text-align: center;
                }
            </style>

            <div class="wrapper">
                <video autoplay playsinline muted></video>
                <div class="viewfinder">
                    <div class="corner tl"></div>
                    <div class="corner tr"></div>
                    <div class="corner bl"></div>
                    <div class="corner br"></div>
                    <div class="scan-line"></div>
                </div>
            </div>
        `;
    }

    // ─── Camera + scanning ────────────────────────────────────────────────────

    async _start() {
        if ('BarcodeDetector' in window) {
            this._detector = new BarcodeDetector({ formats: ['qr_code'] });
        } else {
            this._canvas = document.createElement('canvas');
        }

        try {
            this._stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: { ideal: 'environment' } }
            });

            const video = this.root.querySelector<HTMLVideoElement>('video')!;
            video.srcObject = this._stream;

            this._scanTimer = window.setInterval(() => this._scan(video), 250);
        } catch (err) {
            console.error('[hat-qr-reader] camera error', err);
            this.root.innerHTML = `<div class="unsupported">Could not access camera.</div>`;
        }
    }

    async _scan(video: HTMLVideoElement) {
        if (video.readyState < 2) return;

        if (this._detector) {
            try {
                const results = await this._detector.detect(video);
                if (results.length > 0) this._emit(results[0].rawValue);
            } catch {
                // frame decode errors are non-fatal
            }
            return;
        }

        if (this._canvas) {
            const { videoWidth: w, videoHeight: h } = video;
            if (!w || !h) return;
            this._canvas.width = w;
            this._canvas.height = h;
            const ctx = this._canvas.getContext('2d')!;
            ctx.drawImage(video, 0, 0, w, h);
            const imageData = ctx.getImageData(0, 0, w, h);
            const result = jsQR(imageData.data, w, h);
            if (result) this._emit(result.data);
        }
    }

    _emit(value: string) {
        this.dispatchEvent(new CustomEvent('qr-detected', {
            detail: { value },
            bubbles: true,
            composed: true,
        }));
    }

    _stop() {
        if (this._scanTimer !== null) {
            clearInterval(this._scanTimer);
            this._scanTimer = null;
        }
        this._stream?.getTracks().forEach(t => t.stop());
        this._stream = null;
    }
}

customElements.define('hat-qr-reader', HatQrReader);